import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategoryProducts, useWishlist, useBrands } from '../hooks/useProducts';
import ProductGrid from '../components/product/ProductGrid';
import { useAuthStore } from '../stores/authStore';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [page, setPage] = useState(1);
  const [selectedBrand, setSelectedBrand] = useState('');
  const { data: brands } = useBrands();
  const isRTL = i18n.dir() === 'rtl';

  const { data, isLoading, error } = useCategoryProducts(slug || '', page, selectedBrand || undefined);
  const { data: wishlist } = useWishlist();

  const wishlistIds = wishlist?.map((item) => item.id) || [];

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('common.error')}</h1>
        <p className="text-gray-500 mt-2">{t('common.noResults')}</p>
        <Link to="/" className="btn btn-primary mt-4">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  const category = data?.category;
  const products = data?.products?.products || [];
  const pagination = data?.products?.pagination;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-4">
        <Link to="/" className="hover:text-primary">{t('nav.home')}</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{category?.name || slug}</span>
      </nav>

      {/* Category Header */}
      <div className="flex items-center gap-3 mb-6">
        {category?.icon && (
          <span className="text-3xl">{category.icon}</span>
        )}
        <h1 className="text-2xl font-bold">
          {category?.name || slug}
        </h1>
        {pagination && (
          <span className="text-gray-500 text-sm">
            ({pagination.total} {t('product.noProducts').replace('לא נמצאו ', '').replace('No products found', 'products')})
          </span>
        )}
      </div>

      {/* Brand Filter */}
      {brands && brands.length > 0 && (
        <div className="mb-6">
          <select
            value={selectedBrand}
            onChange={(e) => { setSelectedBrand(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="">{isRTL ? 'כל המותגים' : 'All Brands'}</option>
            {brands.map((brand) => (
              <option key={brand} value={brand}>{brand.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      )}

      {/* Products Grid */}
      <ProductGrid
        products={products}
        wishlistIds={isAuthenticated ? wishlistIds : []}
        isLoading={isLoading}
        emptyMessage={t('product.noProducts')}
      />

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-8">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t('common.previous')}
          </button>

          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === pagination.totalPages || Math.abs(p - page) <= 2)
            .map((p, idx, arr) => (
              <span key={p}>
                {idx > 0 && arr[idx - 1] !== p - 1 && (
                  <span className="px-2">...</span>
                )}
                <button
                  onClick={() => setPage(p)}
                  className={`px-4 py-2 border rounded-lg ${
                    p === page
                      ? 'bg-primary text-white border-primary'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              </span>
            ))}

          <button
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}
